import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { CountUpDirective } from '../../directives/count-up.directive';
import { INDUSTRIES } from '../../data/industries.data';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-industries',
  standalone: true,
  imports: [CommonModule, MatIconModule, CountUpDirective, TranslateModule],
  template: `
    <section class="section industries-section">
      <div class="container">
        <div class="header">
          <h2 class="section-title text-center">{{ sectionTitle }}</h2>
          <div class="industry-count">
            <span countUp [endValue]="65" class="count">0</span>
            <span class="plus">+</span>
            <span class="label">{{ countLabel }}</span>
          </div>
        </div>

        <!-- Force slider container to use LTR direction regardless of language -->
        <div class="industries-slider" dir="ltr">
          <div class="slider-track">
            <div class="industry-card" *ngFor="let industry of industries">
              <mat-icon class="industry-icon">{{ industry.icon }}</mat-icon>
              <h3>{{ industry.label | translate }}</h3>
            </div>
            <div class="industry-card" *ngFor="let industry of industries">
              <mat-icon class="industry-icon">{{ industry.icon }}</mat-icon>
              <h3>{{ industry.label | translate }}</h3>
            </div>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .section-title {
      font-size: 2.5rem;
      margin-bottom: 1.5rem;
      font-weight: 700;
      text-align: center;
      color: #300458;
    }

    .section-subtitle {
      font-size: 1.2rem;
      color: var(--light-text);
      margin-bottom: 3rem;
      text-align: center;
      color: #000;
    }

    .industries-section {
      background: white;
      overflow: hidden;
      padding: 3rem 0;
    }

    .header {
      text-align: center;
      margin-bottom: 4rem;
    }

    .industry-count {
      display: flex;
      align-items: baseline;
      justify-content: center;
      gap: 0.5rem;
      margin-top: 2rem;
    }

    .count {
      font-size: 5rem;
      font-weight: 700;
      color: #6a1b9a;
      line-height: 1;
    }

    .plus {
      font-size: 3.5rem;
      font-weight: 700;
      color: #6a1b9a;
    }

    .label {
      font-size: 2.5rem;
      color: #6a1b9a;
      margin-left: 1rem;
      font-weight: 600;
      letter-spacing: 1px;
    }

    .industries-slider {
      display: flex;
      width: 100%;
      overflow: hidden;
      position: relative;
      mask: linear-gradient(90deg, transparent, white 5%, white 95%, transparent);
      -webkit-mask: linear-gradient(90deg, transparent, white 5%, white 95%, transparent);
    }

    .slider-track {
      display: flex;
      flex-shrink: 0;
      gap: 2rem;
      padding: 1rem 0;
      pointer-events: none;
      will-change: transform;
      min-width: max-content;
      animation: scroll 40s linear infinite;
    }

    .industry-card {
      flex: 0 0 250px;
      padding: 2rem;
      background: var(--background-color, #fff);
      border-radius: var(--border-radius, 8px);
      text-align: center;
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .industry-card:hover {
      transform: translateY(-5px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      background: #6a1b9a;
    }

    .industry-card:hover .industry-icon,
    .industry-card:hover h3 {
      color: white;
    }

    .industry-icon {
      font-size: 2.5rem;
      width: 2.5rem;
      height: 2.5rem;
      color: #6a1b9a;
      margin-bottom: 1rem;
      transition: color 0.3s ease;
    }

    h3 {
      font-size: 1.1rem;
      color: var(--text-color);
      margin: 0;
      transition: color 0.3s ease;
    }

    @keyframes scroll {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(calc(-100% - 2rem)); /* account for gap */
      }
    }

    @media (max-width: 768px) {
      .industries-section {
        padding: 4rem 0;
      }

      .count {
        font-size: 4rem;
      }

      .plus {
        font-size: 3rem;
      }

      .label {
        font-size: 2rem;
      }
    }
  `]
})
export class IndustriesComponent implements OnInit, AfterViewInit {
  sectionTitle: string = '';
  countLabel: string = '';
  industries = INDUSTRIES;

  @ViewChild('sliderTrack') sliderTrack!: ElementRef;

  constructor(public _translate: TranslateService) { }

  ngOnInit(): void {
    this.sectionTitle = this._translate.instant('INDUSTRIES.SECTION_TITLE');
    this.countLabel = this._translate.instant('INDUSTRIES.COUNT_LABEL');
  }

  ngAfterViewInit(): void {
    // Calculate and set width for both tracks
    const tracks = document.querySelectorAll('.slider-track');
    tracks.forEach(track => {
      const totalWidth = this.industries.length * (250 + 32); // card width + gap
      (track as HTMLElement).style.width = `${totalWidth}px`;
    });
  }
}
